import { faListCheck, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV4,
  ButtonV2,
  LabraToolTipV2,
  RadioButtonGroupMapping,
  RadioButtonsV2,
  RadioButtonType,
} from '@labrav/react-components'
import { FieldArray, FormikProps, useFormikContext } from 'formik'
import {
  AzureOfferPlan,
  AzurePricingType,
} from '../../../../modules/privateOffer/types'
import { useStyles } from '../Plan/Plan.styles'
import { Product } from '../../../../modules/productsListing/types'
import { useCallback, useMemo } from 'react'
import { isEmpty } from 'lodash'
import clsx from 'clsx'
import {
  MicrosoftFormFAQs,
  MicrosoftFormFAQsItem,
} from '../MicrosoftFormFAQs/MicrosoftFormFAQs'
import { ContainerPlanRow } from './ContainerPlanRow'
import {
  isAzureContainerProduct,
  resetContainerPlansForPricingType,
} from '../../../../components/MicrosoftFormComponent/ContainerPlan/containerPlanUtils'
import { isAzureVmProduct } from '../../../../components/MicrosoftFormComponent/VmPlan/vmPlanUtils'
import { Plan } from '../Plan/Plan'
import { VmPlan } from '../VmPlan/VmPlan'

const pricingTypeMapping: RadioButtonGroupMapping[] = [
  {
    key: AzurePricingType.ABSOLUTE,
    label: 'Absolute',
    type: RadioButtonType.STANDARD,
  },
  {
    key: AzurePricingType.LABRA_PERCENTAGE,
    label: 'Discount',
    type: RadioButtonType.STANDARD,
  },
]

const emptyPlan = {
  displayName: '',
  planId: '',
  offerPricingType: 'saasNewCustomizedPlans',
  description: '',
  labraPlanId: '',
  discountPercentage: null,
  pricing: {
    recurrentPrice: {
      recurrentPriceMode: '',
      priceInputOption: 'usd',
      prices: [],
    },
  },
}

export const ContainerPlan = ({ product }: { product: Product }) => {
  const { classes } = useStyles()
  const { values, setFieldValue, errors, touched } =
    useFormikContext() as FormikProps<Record<string, unknown>>

  const plans = (values?.plans || []) as AzureOfferPlan[]
  const planIds = useMemo(
    () => plans.map(plan => plan?.labraPlanId || '').filter(id => !isEmpty(id)),
    [plans]
  )

  const handleSelection = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value

      if (value) {
        if (value !== values.azurePricingType) {
          const updatedPlans = resetContainerPlansForPricingType({
            plans,
            product,
          })
          await setFieldValue('plans', updatedPlans)
          await setFieldValue('azurePricingType', value)
        } else {
          await setFieldValue('azurePricingType', value)
        }
      } else {
        await setFieldValue('azurePricingType', values.azurePricingType)
      }
    },
    [plans, product, setFieldValue, values.azurePricingType]
  )

  const isDisablePlanAdd = (product?.plans || [])?.length === plans?.length

  return (
    <BoxV4
      heading={'Pricing configuration'}
      headingIcon={
        <FontAwesomeIcon
          icon={faListCheck}
          style={{ width: '16px', height: '16px' }}
        />
      }
      secondaryActions={
        <MicrosoftFormFAQs item={MicrosoftFormFAQsItem.PRICING} />
      }
    >
      <div className={classes.sectionContainer}>
        <div className={clsx(classes.widthRadio, classes.radioSelection)}>
          <RadioButtonsV2
            name={'azurePricingType'}
            checkedValue={{
              key: (values.azurePricingType || '') as string,
              value: (values.azurePricingType || '') as string,
            }}
            handleChange={handleSelection}
            radioButtonMappings={pricingTypeMapping}
          />

          {!!touched?.azurePricingType && errors?.azurePricingType && (
            <div className={classes.errorMessage}>
              {errors?.azurePricingType}
            </div>
          )}
        </div>

        <FieldArray name="plans">
          {({ push, remove }) => (
            <>
              {plans.map((planItem, index) => (
                <ContainerPlanRow
                  key={planItem?.labraPlanId || `container-plan-${index}`}
                  plan={planItem}
                  remove={remove}
                  index={index}
                  product={product}
                  planIds={planIds}
                />
              ))}
              <LabraToolTipV2
                tooltipPlacement="top"
                description={
                  isDisablePlanAdd
                    ? 'All your existing plans have been added above.'
                    : ''
                }
              >
                <ButtonV2
                  className={classes.widthButton}
                  styleType="link"
                  onClick={() => {
                    push(emptyPlan)
                  }}
                  data-testid="add-a-metaData-button"
                  disabled={isDisablePlanAdd}
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                    width={'14px'}
                    height={'16px'}
                    style={{ paddingRight: '13px', paddingLeft: '5px' }}
                  />
                  Add plan
                </ButtonV2>
              </LabraToolTipV2>
            </>
          )}
        </FieldArray>
      </div>
    </BoxV4>
  )
}

export const PricingConfiguration = ({
  product,
  validate = false,
}: {
  product: Product
  validate?: boolean
}) => {
  if (isAzureContainerProduct(product)) {
    return <ContainerPlan product={product} />
  }

  if (isAzureVmProduct(product)) {
    return <VmPlan product={product} />
  }

  return <Plan product={product} validate={validate} />
}
